import { EmptyContent } from "@/common/EmptyContent";
import { Pane, SplitPane } from "@/common/SplitPane";
import { SourceEditor, createSourcePlugins } from "@/common/editor/RuleAuthor/SourceEditor";
import { FixedRichTextToolbar } from "@/common/editor/RuleAuthor/toolbar/FixedRichTextToolbar";
import { FloatingToolbar } from "@/common/editor/RuleAuthor/toolbar/FloatingToolbar";
import { RichTextToollbar } from "@/common/editor/RuleAuthor/toolbar/basicToolbar";
import { scrollable } from "@/common/scrollbar";
import { ExperimentalLabel } from "@/components";
import { createAppResourceService } from "@/global";
import { type DialogAction, DialogActions } from "@/ui/dialogs/actions";
import { useDialogModal } from "@/ui/dialogs/useDialogModal";
import styled from "@emotion/styled";
import { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { PlateProvider } from "@udecode/plate";
import { once } from "lodash";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { getSourceText } from "./getSourceText";

const SUGGEST_EDITOR_ID = "source-suggest";
// const sourcePlugins = createSourcePlugins();
const sourcePlugins = once(() => createSourcePlugins());

const Panes = styled(SplitPane)`
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  flex: 1;
  max-height: calc(100% - 3.5rem);
`;

const Suggestions = styled.div`
  padding: 0.5rem 1rem;
`;

const SuggestModal = ({ source, releaseId, onInsert }) => {
  const dialogContext = useDialogContext();
  const { t } = useTranslation();

  const [suggestion, setSuggestion] = useState();
  const [newSource, setNewSource] = useState(() => {
    if (Array.isArray(source)) return source;
    return source.type === "section" ? source.children : [source];
  });
  // We do not yet support custom functions with id, workaround is use the subresource system
  const relativeReleaseService = createAppResourceService<any>(`releases/${releaseId}`);
  const { isLoading, mutateAsync } = relativeReleaseService.usePostCustom("suggestSource");

  useEffect(() => {
    if (!suggestion) {
      // Build the source into something the backend will understand
      const text = getSourceText(source);
      // Ask the server to suggest additional content
      console.log("requesting suggestion for", text);

      mutateAsync({ source: text }).then((res) => {
        console.log("AI generated:", res);
        setSuggestion(res.suggestion);
      });
    }
  }, [suggestion]);

  const primaryAction: DialogAction = {
    name: t("insert"),
    onClick: () => {
      onInsert(newSource);
      dialogContext?.close();
    },
  };

  const paneProps = {
    sizes: [50, 50],
    minSize: [300, 300],
    // gutterSize: 8,
    gutterStyle: () => ({ display: "block", width: "8px" }),
  };

  const editableProps = {
    autoFocus: true,
    spellCheck: false,
  };

  return (
    <>
      <Panes
        {...paneProps}
        snapOffset={0}
      >
        <Pane>
          {isLoading ? (
            <EmptyContent
              width="100px"
              messages={[t("ai.generating_message")]}
            />
          ) : (
            <Suggestions className={scrollable}>
              <Markdown>{suggestion}</Markdown>
            </Suggestions>
          )}
        </Pane>
        <Pane>
          <PlateProvider
            id={SUGGEST_EDITOR_ID}
            plugins={sourcePlugins()}
            value={newSource}
            onChange={setNewSource}
          >
            <FixedRichTextToolbar />
            <SourceEditor
              id={SUGGEST_EDITOR_ID}
              editableProps={editableProps}
            >
              <FloatingToolbar>
                <RichTextToollbar />
              </FloatingToolbar>
            </SourceEditor>
          </PlateProvider>
        </Pane>
      </Panes>
      <DialogActions primary={primaryAction} />
    </>
  );
};

export const useSuggestModal = () => {
  const { openDialogModal } = useDialogModal();
  return ({ source, releaseId, onInsert }) => {
    openDialogModal(
      {
        title: (
          <div className="flex items-center gap-4">
            <span>
              <Trans i18nKey="ai.suggest_source_description" />
            </span>
            <ExperimentalLabel />
          </div>
        ),
        maxWidth: "xl",
      },
      <SuggestModal
        source={source}
        releaseId={releaseId}
        onInsert={onInsert}
      />,
    );
  };
};
